import {
  Box,
  Heading,
  Text,
  Code,
  Link,
  List,
  Separator,
  Table,
} from "@chakra-ui/react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfmNoAutolink from "./remarkGfmNoAutolink.js";
import remarkTelLinks from "./remarkTelLinks.js";

// react-markdown blanks any href outside its safe-protocol list, and tel: is
// not on it; remarkTelLinks only emits digits after it.
const urlTransform = (url) =>
  url.startsWith("tel:") ? url : defaultUrlTransform(url);

// Anchor ids for h2/h3, keyed by the heading's source line, so a section
// can be linked (#how-to-book) and quoted by its own address. Computed from
// the markdown string, not during render, so the server and the browser
// produce the same ids.
function headingIds(markdown) {
  const ids = new Map();
  const seen = new Map();
  (typeof markdown === "string" ? markdown : "")
    .split("\n")
    .forEach((line, index) => {
      const match = /^#{2,3}\s+(.+?)\s*#*\s*$/.exec(line);
      if (!match) return;
      const base =
        match[1]
          .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
          .normalize("NFKD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .replace(/['’]/g, "")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "") || "section";
      const count = seen.get(base) || 0;
      seen.set(base, count + 1);
      ids.set(index + 1, count ? `${base}-${count + 1}` : base);
    });
  return ids;
}

const MarkdownRenderer = ({ children, ...props }) => {
  const ids = headingIds(children);
  const idFor = (node) => ids.get(node?.position?.start?.line);

  const components = {
    h2: ({ node, children, ...props }) => (
      <Heading
        as="h2"
        id={idFor(node)}
        size="sm"
        mb={4}
        mt={12}
        pt={6}
        borderTopWidth="1px"
        borderColor="border"
        {...props}
      >
        {children}
      </Heading>
    ),
    h3: ({ node, children, ...props }) => (
      <Heading as="h3" id={idFor(node)} size="xs" mb={3} mt={8} {...props}>
        {children}
      </Heading>
    ),

    p: ({ children, ...props }) => (
      <Text mb={4} lineHeight="1.75" {...props}>
        {children}
      </Text>
    ),

    a: ({ href, children, ...props }) => (
      <Link
        href={href}
        color="accent"
        textDecoration="underline"
        textDecorationColor="border"
        textUnderlineOffset="2px"
        _hover={{ textDecorationColor: "accent" }}
        {...props}
      >
        {children}
      </Link>
    ),

    // react-markdown wraps block code in pre; code itself must stay inline-safe.
    pre: ({ children, ...props }) => (
      <Box
        as="pre"
        bg="bg.muted"
        p={4}
        rounded="md"
        mb={4}
        overflowX="auto"
        fontSize="sm"
        css={{ "& > code": { bg: "transparent", p: 0, fontSize: "inherit" } }}
        {...props}
      >
        {children}
      </Box>
    ),
    code: ({ children, ...props }) => (
      <Code colorPalette="gray" fontSize="sm" px={1} py={0.5} {...props}>
        {children}
      </Code>
    ),

    ul: ({ children, ...props }) => (
      <List.Root mb={4} pl={4} {...props}>
        {children}
      </List.Root>
    ),
    ol: ({ children, ...props }) => (
      <List.Root as="ol" mb={4} pl={4} {...props}>
        {children}
      </List.Root>
    ),
    li: ({ children, ...props }) => (
      <List.Item mb={1} {...props}>
        {children}
      </List.Item>
    ),

    hr: ({ ...props }) => <Separator my={8} {...props} />,

    img: ({ src, alt, ...props }) => (
      <Box
        as="img"
        src={src}
        alt={alt}
        w="full"
        rounded="md"
        mb={4}
        {...props}
      />
    ),

    table: ({ children, ...props }) => (
      <Box overflowX="auto" mb={6}>
        <Table.Root size="sm" {...props}>
          {children}
        </Table.Root>
      </Box>
    ),
    thead: ({ children, ...props }) => (
      <Table.Header {...props}>{children}</Table.Header>
    ),
    tbody: ({ children, ...props }) => (
      <Table.Body {...props}>{children}</Table.Body>
    ),
    tr: ({ children, ...props }) => (
      <Table.Row {...props}>{children}</Table.Row>
    ),
    th: ({ children, ...props }) => (
      <Table.ColumnHeader fontSize="xs" whiteSpace="normal" {...props}>
        {children}
      </Table.ColumnHeader>
    ),
    td: ({ children, ...props }) => (
      <Table.Cell fontSize="sm" whiteSpace="normal" {...props}>
        {children}
      </Table.Cell>
    ),

    // Blockquote (used for callouts and mid-article CTAs)
    blockquote: ({ children, ...props }) => (
      <Box
        as="blockquote"
        borderLeft="4px"
        borderColor="accent"
        pl={5}
        pr={5}
        py={4}
        my={8}
        bg="bg.subtle"
        rounded="md"
        css={{ "& > p:last-child": { mb: 0 } }}
        {...props}
      >
        {children}
      </Box>
    ),
  };

  // react-markdown hands every component its hast `node`; spread onto a DOM
  // element it renders as node="[object Object]". Only the headings read it
  // (for their ids, without spreading it), so every other override gets the
  // props without it.
  const domSafe = Object.fromEntries(
    Object.entries(components).map(([tag, Component]) => [
      tag,
      tag === "h2" || tag === "h3"
        ? Component
        : ({ node: _node, ...rest }) => Component(rest),
    ]),
  );

  return (
    <Box {...props}>
      <ReactMarkdown
        components={domSafe}
        remarkPlugins={[remarkGfmNoAutolink, remarkTelLinks]}
        urlTransform={urlTransform}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
};

export { MarkdownRenderer };
